import styled from 'styled-components';
import getThemeByProps from '@userzoomlibraries/toolkit-theme-manager/get-theme-by-props';
import React, { useState, useRef, useCallback, useEffect, useMemo, useImperativeHandle } from 'react';
import noop from '@userzoomlibraries/toolkit-js-utils/noop';
import PropTypes from '@userzoomlibraries/toolkit-js-utils/prop-types';
import useInterval from '@userzoomlibraries/toolkit-react-utils/use-interval';
import { jsxs, jsx } from 'react/jsx-runtime';
import isDefinedAndNotNull from '@userzoomlibraries/toolkit-js-utils/is-defined-and-not-null';
import isNumber from '@userzoomlibraries/toolkit-js-utils/is-number';
import isFunction from '@userzoomlibraries/toolkit-js-utils/is-function';
import isEqual from '@userzoomlibraries/toolkit-js-utils/is-equal';
import useComparePrevious from '@userzoomlibraries/toolkit-react-utils/use-compare-previous';
import { I as InputText } from './index.styled2.js';
import { N as NUMBER_DEFAULT_PROPS, I as INPUT_MODE, d as NUMBER_PROP_TYPES } from './index.styled8.js';

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var isLeftMouseClick = function isLeftMouseClick(event) {
  var finalEvent = event.nativeEvent || event;
  /*
    Which values:
    * 1 = Left mouse button
    * 2 = Middle mouse button
    * 3 = Right mouse button
  */

  return finalEvent.which === 1;
};

function _slicedToArray$2(arr, i) { return _arrayWithHoles$2(arr) || _iterableToArrayLimit$2(arr, i) || _unsupportedIterableToArray$2(arr, i) || _nonIterableRest$2(); }

function _nonIterableRest$2() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$2(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$2(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$2(o, minLen); }

function _arrayLikeToArray$2(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit$2(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles$2(arr) { if (Array.isArray(arr)) return arr; }
var CHANGE_INTERVAL = 75;
var TIMEOUT_TO_START_CHANGE = 300;

var NumberSuffix$1 = function NumberSuffix(_ref) {
  var className = _ref.className,
      disabled = _ref.disabled,
      increase = _ref.increase,
      decrease = _ref.decrease;

  var _useState = useState({
    runOnce: true,
    actionToExecute: null
  }),
      _useState2 = _slicedToArray$2(_useState, 2),
      _useState2$ = _useState2[0],
      runOnce = _useState2$.runOnce,
      actionToExecute = _useState2$.actionToExecute,
      setSettings = _useState2[1];

  var timeoutRef = useRef();

  var removeTimeout = function removeTimeout() {
    return clearTimeout(timeoutRef.current);
  };

  var intervalCallback = useCallback(function () {
    if (actionToExecute === 'increase') {
      increase();
    }

    if (actionToExecute === 'decrease') {
      decrease();
    }
  }, [actionToExecute, increase, decrease]);

  var _useInterval = useInterval(intervalCallback, CHANGE_INTERVAL, false),
      startInterval = _useInterval.start,
      stopInterval = _useInterval.stop;

  var increaseHandler = useCallback(function (event) {
    if (!disabled && isLeftMouseClick(event)) {
      if (runOnce) {
        increase();
      }

      timeoutRef.current = setTimeout(function () {
        setSettings({
          runOnce: false,
          actionToExecute: 'increase'
        });
        startInterval();
      }, TIMEOUT_TO_START_CHANGE);
    }
  }, [disabled, runOnce, increase, startInterval]);
  var decreaseHandler = useCallback(function (event) {
    if (!disabled && isLeftMouseClick(event)) {
      if (runOnce) {
        decrease();
      }

      timeoutRef.current = setTimeout(function () {
        setSettings({
          runOnce: false,
          actionToExecute: 'decrease'
        });
        startInterval();
      }, TIMEOUT_TO_START_CHANGE);
    }
  }, [disabled, runOnce, decrease, startInterval]);
  var stop = useCallback(function () {
    setSettings({
      runOnce: true
    });
    stopInterval();
    removeTimeout();
  }, [stopInterval]);
  useEffect(function () {
    return function () {
      return removeTimeout();
    };
  }, []);
  return /*#__PURE__*/jsxs("div", {
    tabIndex: "-1",
    className: "stepper ".concat(className),
    "aria-disabled": disabled,
    children: [/*#__PURE__*/jsx("span", {
      tabIndex: "-1",
      role: "button",
      "aria-disabled": disabled,
      className: "increase-button",
      onTouchStart: increaseHandler,
      onMouseDown: increaseHandler,
      onMouseUp: stop,
      onMouseLeave: stop,
      onTouchEnd: stop,
      children: "+"
    }), /*#__PURE__*/jsx("span", {
      tabIndex: "-1",
      role: "button",
      "aria-disabled": disabled,
      className: "decrease-button",
      onTouchStart: decreaseHandler,
      onMouseDown: decreaseHandler,
      onMouseUp: stop,
      onMouseLeave: stop,
      onTouchEnd: stop,
      children: "-"
    })]
  });
};

NumberSuffix$1.propTypes = {
  className: PropTypes.string,
  increase: PropTypes.func,
  decrease: PropTypes.func,
  disabled: PropTypes.bool
};
NumberSuffix$1.defaultProps = {
  className: '',
  increase: noop,
  decrease: noop,
  disabled: false
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var NumberSuffix = styled(NumberSuffix$1).withConfig({
  componentId: "gz-input-1679042055220__sc-dk0grv-0"
})(["border-left:1px solid #000;height:100%;margin:0;overflow:hidden;padding:0;.increase-button,.decrease-button{appearance:none;background-color:transparent;border:none;display:block;height:50%;line-height:1;margin:0;overflow:hidden;padding:0 6px;text-align:center;user-select:none;width:100%;&:first-child{border-bottom:1px solid #000;}&:not([aria-disabled=\"true\"]){&:hover{background-color:#474b6d;color:#fff;cursor:pointer;}}}&[aria-disabled=\"true\"]{cursor:not-allowed;pointer-events:none;}"]);

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var decimalCharacterRegex = /^[Ee0-9+\-.]$/;

var isDecimalCharacter = function isDecimalCharacter(value) {
  return decimalCharacterRegex.test(value);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var isValidNumericEvent = function isValidNumericEvent(event) {
  var keyPressed = event.key;

  if (keyPressed === null) {
    return true;
  }

  var isModifierKey = event.ctrlKey || event.altKey || event.metaKey;

  if (isModifierKey) {
    return true;
  }

  var isOneCharKey = keyPressed.length === 1;
  var isUpArrowPressed = keyPressed === 'ArrowUp' || event.code === 'ArrowUp' || event.keyCode === 38;
  var isDownArrowPressed = keyPressed === 'ArrowDown' || event.code === 'ArrowDown' || event.keyCode === 40;

  if (!isOneCharKey && !isUpArrowPressed && !isDownArrowPressed) {
    return true;
  }

  var isDecimalChar = isDecimalCharacter(keyPressed);
  var isFirstChar = event.target.selectionStart === 0;
  var lastCharOfValue = event.target.value.charAt(event.target.value.length - 1);

  if (isDecimalChar) {
    if (isFirstChar && (keyPressed === '-' || keyPressed === '+')) {
      var existSameChar = event.target.value.split('').some(function (each) {
        return ['+', '-'].includes(each);
      });
      return !existSameChar;
    }

    if (isFirstChar && keyPressed === '.') {
      return false;
    }

    if (!isFirstChar && (keyPressed === '-' || keyPressed === '+')) {
      return false;
    }

    if (!isFirstChar && keyPressed === '.') {
      var _existSameChar = event.target.value.split('').some(function (each) {
        return each === '.';
      });

      if (lastCharOfValue === keyPressed || _existSameChar) {
        return false;
      }
    }

    return true;
  }

  return false;
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var sanitizeNumericValue = function sanitizeNumericValue(value) {
  return value.split('').filter(isDecimalCharacter).join('');
};

function _slicedToArray$1(arr, i) { return _arrayWithHoles$1(arr) || _iterableToArrayLimit$1(arr, i) || _unsupportedIterableToArray$1(arr, i) || _nonIterableRest$1(); }

function _nonIterableRest$1() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$1(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$1(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$1(o, minLen); }

function _arrayLikeToArray$1(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit$1(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles$1(arr) { if (Array.isArray(arr)) return arr; }

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var getPrecision = function getPrecision(value) {
  var _value$toString$split = value.toString().split('.'),
      _value$toString$split2 = _slicedToArray$1(_value$toString$split, 2),
      _value$toString$split3 = _value$toString$split2[1],
      decimals = _value$toString$split3 === void 0 ? '' : _value$toString$split3;

  return decimals.length;
};

var _excluded = ["defaultValue", "value", "disabled", "precision", "min", "max", "step", "prefix", "suffix", "noStepper", "onChange", "onBlur"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var InputNumber$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var defaultValue = _ref.defaultValue,
      value = _ref.value,
      disabled = _ref.disabled,
      precision = _ref.precision,
      min = _ref.min,
      max = _ref.max,
      step = _ref.step,
      prefix = _ref.prefix,
      suffix = _ref.suffix,
      noStepper = _ref.noStepper,
      onChange = _ref.onChange,
      onBlur = _ref.onBlur,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(function () {
    var newValue = '';

    if (isNumber(defaultValue)) {
      newValue = defaultValue;
    }

    if (isNumber(value)) {
      newValue = value;
    }

    if (isNumber(newValue)) {
      newValue = newValue.toFixed(precision);
    }

    return newValue.toString();
  }),
      _useState2 = _slicedToArray(_useState, 2),
      stateValue = _useState2[0],
      setStateValue = _useState2[1];

  var inputNumberRef = useRef();
  var isControlledInput = useMemo(function () {
    if (isDefinedAndNotNull(defaultValue) && !isDefinedAndNotNull(value)) {
      return false;
    }

    if (!isDefinedAndNotNull(defaultValue) && isDefinedAndNotNull(value)) {
      return true;
    }

    if (!isDefinedAndNotNull(defaultValue) && !isDefinedAndNotNull(value)) {
      return false;
    }

    console.error("Warning: ".concat(InputNumber$1.displayName, " contains an input with both value and defaultValue props.\n      Input elements must be either controlled or uncontrolled (specify either the value prop, or the defaultValue prop, but not both).\n      Decide between using a controlled or uncontrolled input element and remove one of these props. More info: https://reactjs.org/link/controlled-components"));
    return false;
  }, [useComparePrevious(value), useComparePrevious(defaultValue)]);
  var increase = useCallback(function () {
    var forceMax = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

    if (!disabled) {
      var currentValueAsNumber = parseFloat(stateValue || 0);
      var precisionToUse = precision || Math.max(getPrecision(currentValueAsNumber), getPrecision(step));
      var newValueAsNumber;

      if (forceMax) {
        newValueAsNumber = max;
      } else {
        newValueAsNumber = currentValueAsNumber + step;

        if (isDefinedAndNotNull(max) && newValueAsNumber > max) {
          newValueAsNumber = max;
        } else if (isDefinedAndNotNull(min) && newValueAsNumber < min) {
          newValueAsNumber = min;
        }
      }

      newValueAsNumber = parseFloat(newValueAsNumber.toFixed(precisionToUse));

      if (newValueAsNumber !== currentValueAsNumber) {
        if (!isControlledInput) {
          setStateValue(newValueAsNumber.toFixed(precisionToUse));
        }

        onChange({
          value: newValueAsNumber
        });
      }
    }
  }, [stateValue, isControlledInput, precision, disabled, min, max, step, onChange]);
  var decrease = useCallback(function () {
    var forceMin = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

    if (!disabled) {
      var currentValueAsNumber = parseFloat(stateValue || 0);
      var precisionToUse = precision || Math.max(getPrecision(currentValueAsNumber), getPrecision(step));
      var newValueAsNumber;

      if (forceMin) {
        newValueAsNumber = min;
      } else {
        newValueAsNumber = currentValueAsNumber - step;

        if (isDefinedAndNotNull(max) && newValueAsNumber > max) {
          newValueAsNumber = max;
        } else if (isDefinedAndNotNull(min) && newValueAsNumber < min) {
          newValueAsNumber = min;
        }
      }

      newValueAsNumber = parseFloat(newValueAsNumber.toFixed(precisionToUse));

      if (newValueAsNumber !== currentValueAsNumber) {
        if (!isControlledInput) {
          setStateValue(newValueAsNumber.toFixed(precisionToUse));
        }

        onChange({
          value: newValueAsNumber
        });
      }
    }
  }, [stateValue, isControlledInput, precision, disabled, min, max, step, onChange]);
  var validateAndAdjustValue = useCallback(function () {
    var initialValue = stateValue;
    var finalValue;
    var finalValueAsNumber;

    if (isDefinedAndNotNull(stateValue) && stateValue.length) {
      var valueAsNumber = +stateValue;
      var newValue = valueAsNumber;

      if (isDefinedAndNotNull(min) && valueAsNumber < min) {
        newValue = min;
      }

      if (isDefinedAndNotNull(max) && valueAsNumber > max) {
        newValue = max;
      }

      var parsedValue = parseFloat(newValue.toFixed(precision));
      finalValueAsNumber = parsedValue;

      if (Number.isNaN(parsedValue)) {
        finalValue = '';
      } else {
        finalValue = parsedValue.toFixed(precision);
      }

      setStateValue(finalValue);
    }

    return {
      initialValue: initialValue,
      finalValue: finalValue,
      finalValueAsNumber: finalValueAsNumber
    };
  }, [stateValue, precision, min, max]);
  var onChangeHandler = useCallback(function (_ref2) {
    var newValue = _ref2.value;
    setStateValue(sanitizeNumericValue(newValue));
    var newValueAsNumber;

    if (newValue.length !== 0) {
      newValueAsNumber = parseFloat((+newValue).toFixed(precision));
    }

    onChange({
      value: newValueAsNumber
    });
  }, [precision, onChange]);
  var onKeyDownHandler = useCallback(function (event) {
    if (!isValidNumericEvent(event)) {
      event.preventDefault();
    }

    var isUpArrowPressed = event.key === 'ArrowUp' || event.code === 'ArrowUp' || event.keyCode === 38;
    var isDownArrowPressed = event.key === 'ArrowDown' || event.code === 'ArrowDown' || event.keyCode === 40;
    var isHomePressed = event.key === 'Home' || event.code === 'Home' || event.keyCode === 36;
    var isEndPressed = event.key === 'End' || event.code === 'End' || event.keyCode === 35;

    if (isUpArrowPressed || isDownArrowPressed || isHomePressed || isEndPressed) {
      if (isUpArrowPressed) {
        increase();
      } else if (isDownArrowPressed) {
        decrease();
      } else if (isHomePressed && isDefinedAndNotNull(min) || isEndPressed && isDefinedAndNotNull(max)) {
        event.preventDefault();

        if (isHomePressed) {
          decrease(isHomePressed);
        } else if (isEndPressed) {
          increase(isEndPressed);
        }
      }
    }
  }, [increase, decrease, min, max]);
  var onBlurHandler = useCallback(function (event) {
    if (!inputNumberRef.current.contains(event.relatedTarget)) {
      var _validateAndAdjustVal = validateAndAdjustValue(),
          initialValue = _validateAndAdjustVal.initialValue,
          finalValue = _validateAndAdjustVal.finalValue,
          finalValueAsNumber = _validateAndAdjustVal.finalValueAsNumber;

      onBlur(event);

      if (!isEqual(initialValue, finalValue)) {
        onChange({
          value: finalValueAsNumber
        });
      }
    }
  }, [onBlur, onChange, validateAndAdjustValue]);
  var prefixToProcess = useMemo(function () {
    if (isFunction(prefix)) {
      return prefix;
    }

    return function () {
      return prefix;
    };
  }, [prefix]);
  var suffixToProcess = useMemo(function () {
    if (!noStepper) {
      if (isFunction(suffix)) {
        return suffix;
      }

      if (isDefinedAndNotNull(suffix)) {
        return function () {
          return suffix;
        };
      }

      return function () {
        return /*#__PURE__*/jsx(NumberSuffix, {
          disabled: disabled,
          increase: increase,
          decrease: decrease
        });
      };
    }

    return function () {
      return null;
    };
  }, [noStepper, suffix, disabled, increase, decrease]);
  useEffect(function () {
    var newValue = '';

    if (isNumber(defaultValue)) {
      newValue = defaultValue;
    }

    if (isNumber(value)) {
      newValue = value;
    }

    if (isNumber(newValue)) {
      newValue = newValue.toFixed(precision);
    }

    setStateValue(newValue.toString());
  }, [precision, value, defaultValue]);
  useImperativeHandle(ref, function () {
    return inputNumberRef.current;
  });
  return /*#__PURE__*/jsx(InputText, _objectSpread(_objectSpread({
    ref: inputNumberRef
  }, otherProps), {}, {
    role: "spinbutton",
    inputMode: INPUT_MODE.DECIMAL,
    prefix: prefixToProcess({
      disabled: disabled,
      invalid: otherProps.invalid,
      value: parseFloat(stateValue),
      min: min,
      max: max,
      step: step,
      precision: precision,
      increase: increase,
      decrease: decrease
    }),
    suffix: suffixToProcess({
      disabled: disabled,
      invalid: otherProps.invalid,
      value: parseFloat(stateValue),
      min: min,
      max: max,
      step: step,
      precision: precision,
      increase: increase,
      decrease: decrease
    }),
    value: stateValue,
    disabled: disabled,
    "aria-valuemin": min,
    "aria-valuemax": max,
    "aria-valuetext": stateValue,
    "aria-valuenow": stateValue,
    onChange: onChangeHandler,
    onKeyDown: onKeyDownHandler,
    onBlur: onBlurHandler
  }));
});
InputNumber$1.COMPONENT_ID = 'Gearz.Input.Number';
InputNumber$1.displayName = InputNumber$1.COMPONENT_ID;
InputNumber$1.propTypes = _objectSpread({}, NUMBER_PROP_TYPES);
InputNumber$1.defaultProps = _objectSpread(_objectSpread({}, NUMBER_DEFAULT_PROPS), {}, {
  autoComplete: 'off',
  maxLength: null,
  minLength: null
});

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var InputNumber = styled(InputNumber$1).withConfig({
  componentId: "gz-input-1679042055220__sc-1xc98mj-0"
})(["background-color:#fff;border:1px solid #000;border-radius:4px;box-sizing:border-box;display:flex;line-height:20px;margin:0px;min-width:0;position:relative;width:100%;.gz-input{-moz-appearance:textfield;&::-webkit-outer-spin-button,&::-webkit-inner-spin-button{appearance:none;margin:0;}}&:not(:focus-within){&:not([aria-disabled=\"true\"]){&[aria-invalid=\"true\"]{border-color:#f00314;&:focus,&:focus-visible,&:hover{border-color:#474b6d;", "{border-left:1px solid #000;.increase-button,.decrease-button{&:hover{background-color:#474b6d;}&:first-child{border-bottom:1px solid #000;}}}}", "{border-left:1px solid #f00314;.increase-button,.decrease-button{&:hover{background-color:transparent;}&:first-child{border-bottom:1px solid #f00314;}}}}}}", ""], NumberSuffix, NumberSuffix, function (props) {
  return getThemeByProps({
    componentId: InputNumber$1.COMPONENT_ID,
    props: props
  });
});
InputNumber.displayName = InputNumber$1.displayName;
InputNumber.propTypes = InputNumber$1.propTypes;
InputNumber.defaultProps = InputNumber$1.defaultProps;

export { InputNumber as I };
